'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';
import { getPublicGallery } from '../actions/publicGallery';
import type { GalleryItem } from '@/contexts/publishing';

const PAGE_SIZE = 24;

const LANG_LABEL: Record<string, string> = {
  ru: 'Русский',
  en: 'English',
  uk: 'Українська',
  de: 'Deutsch',
  fr: 'Français',
  cs: 'Čeština',
};

type Kind = 'audio' | 'video' | undefined;

export function GalleryClient({
  initialItems,
  languages,
  playlists,
  initialLanguages = [],
}: {
  initialItems: GalleryItem[];
  languages: string[];
  /** Pre-picked on arrival — the visitor's own language, when there is anything in it. */
  initialLanguages?: string[];
  /** the channel playlist of each language ("*" = the rest) — one list holds both kinds */
  playlists: Record<string, string>;
}) {
  const locale = useLocale();
  const t = useTranslations('gallery');
  const [items, setItems] = useState<GalleryItem[]>(initialItems);
  const [kind, setKind] = useState<Kind>(undefined);
  // SEVERAL languages at once: a household that reads in Russian and Czech wants
  // both, and a single-choice dropdown made them switch back and forth. Empty = all.
  const [langs, setLangs] = useState<string[]>(initialLanguages);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialItems.length >= PAGE_SIZE);
  const [watchOpen, setWatchOpen] = useState(false);
  // Per-card rendition pick when a story has BOTH an audio and a video publication.
  const [cardKind, setCardKind] = useState<Record<string, 'audio' | 'video'>>({});
  const sentinel = useRef<HTMLDivElement>(null);
  // Bump to cancel stale in-flight loads when filters change.
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (offset: number, replace: boolean, k: Kind, l: string[]) => {
      const my = ++reqId.current;
      setLoading(true);
      const page = await getPublicGallery({ limit: PAGE_SIZE, offset, kind: k, languages: l });
      if (my !== reqId.current) return; // superseded by a newer filter change
      setItems((prev) => (replace ? page : [...prev, ...page]));
      setHasMore(page.length >= PAGE_SIZE);
      setLoading(false);
    },
    [],
  );

  // Re-query from scratch whenever a filter changes.
  useEffect(() => {
    fetchPage(0, true, kind, langs);
  }, [kind, langs, fetchPage]);

  // Infinite scroll.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchPage(items.length, false, kind, langs);
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, items.length, kind, langs, fetchPage]);

  // one playlist per language; the kind chips filter OUR list, not the YouTube one
  // One playlist per language: offered only when the filter names exactly one —
  // "watch continuously" cannot mean two channels at once.
  const watchPlaylistId = (langs.length === 1 && playlists[langs[0]]) || (langs.length === 0 && playlists['*']) || null;

  const Chip = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs whitespace-nowrap',
        active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* Filters — kind + language + watch-continuously */}
      <div className="mb-6 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Chip label={t('filters.all')} active={!kind} onClick={() => setKind(undefined)} />
          <Chip label={`🎧 ${t('filters.audio')}`} active={kind === 'audio'} onClick={() => setKind('audio')} />
          <Chip label={`🎬 ${t('filters.video')}`} active={kind === 'video'} onClick={() => setKind('video')} />

          <div className="ml-auto">
            {watchPlaylistId && (
              <button
                type="button"
                onClick={() => setWatchOpen(true)}
                className="rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                data-testid="gallery-watch-continuous"
              >
                ▶ {t('filters.watchAll')}
              </button>
            )}
          </div>
        </div>

        {/* Languages, several at a time — chips, not a dropdown: a bilingual
            household picks both and sees one list. "All" is the empty pick. */}
        {languages.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="gallery-language-filter">
            <Chip label={`🌐 ${t('filters.allLanguages')}`} active={langs.length === 0} onClick={() => setLangs([])} />
            {languages.map((l) => (
              <Chip
                key={l}
                label={LANG_LABEL[l] ?? l}
                active={langs.includes(l)}
                onClick={() => setLangs((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]))}
              />
            ))}
          </div>
        )}
      </div>

      {items.length === 0 && !loading ? (
        <p className="py-16 text-center text-sm text-muted-foreground">Пока пусто. Загляните позже.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((it) => {
            const kinds = new Set(it.publications.map((p) => p.kind));
            const both = kinds.has('audio') && kinds.has('video');
            const picked: 'audio' | 'video' = both ? (cardKind[it.id] ?? it.kind) : it.kind;
            const pub = it.publications.find((p) => p.kind === picked);
            const videoId = pub?.youtubeVideoId ?? it.youtubeVideoId;
            const href = `/${locale}/gallery/${it.id}${both && picked !== it.kind ? `?kind=${picked}` : ''}`;
            return (
              <div
                key={it.id}
                className="group relative rounded-xl border overflow-hidden transition-shadow hover:shadow-md"
                data-testid={`gallery-card-${it.id}`}
              >
                <Link href={href} className="block">
                  <div className="relative aspect-video bg-muted">
                    {/* Prefer the YouTube thumbnail (served by i.ytimg.com, not our R2)
                        so browsing the gallery doesn't burn our bandwidth and every
                        card counts as a YouTube impression. Fall back to the R2 cover
                        only when there's no video yet. */}
                    {videoId ? (
                      <Image
                        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                        alt={it.title}
                        fill
                        sizes="(max-width: 640px) 50vw, 25vw"
                        className="object-cover transition-transform group-hover:scale-105"
                        unoptimized
                      />
                    ) : it.coverImageUrl ? (
                      <Image
                        src={it.coverImageUrl}
                        alt={it.title}
                        fill
                        sizes="(max-width: 640px) 50vw, 25vw"
                        className="object-cover transition-transform group-hover:scale-105"
                        unoptimized
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-3xl">📖</div>
                    )}
                    {!both && (
                      <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                        {picked === 'audio' ? '🎧' : '🎬'}
                      </span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="line-clamp-2 text-sm font-medium">{it.title}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {LANG_LABEL[it.language] ?? it.language}
                      {it.ageGroup ? ` · ${it.ageGroup}` : ''}
                    </p>
                  </div>
                </Link>
                {both && (
                  <div
                    className="absolute left-1.5 top-1.5 flex overflow-hidden rounded bg-black/60 text-[10px] text-white"
                    data-testid={`gallery-card-kind-switch-${it.id}`}
                  >
                    {(['audio', 'video'] as const).map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setCardKind((prev) => ({ ...prev, [it.id]: k }))}
                        className={cn('px-1.5 py-0.5', picked === k ? 'bg-white/30' : 'opacity-70 hover:opacity-100')}
                        aria-pressed={picked === k}
                      >
                        {k === 'audio' ? '🎧 Аудио' : '🎬 Видео'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {loading && <p className="py-6 text-center text-xs text-muted-foreground">Загрузка…</p>}
      <div ref={sentinel} className="h-1" />

      {/* Watch-continuously — YouTube playlist autoplay (hands-free) */}
      {watchOpen && watchPlaylistId && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/95" data-testid="gallery-watch-modal">
          <div className="flex items-center justify-between p-3">
            <span className="text-sm text-white">Воспроизведение подряд</span>
            <button
              type="button"
              onClick={() => setWatchOpen(false)}
              className="rounded-full bg-white/15 px-4 py-1.5 text-sm text-white hover:bg-white/25"
            >
              Закрыть ✕
            </button>
          </div>
          <div className="relative flex-1">
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube-nocookie.com/embed/videoseries?list=${watchPlaylistId}&autoplay=1&rel=0`}
              title="Owlcat — подряд"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
